import { render } from "preact";
import { useEffect, useRef } from "preact/hooks";
import { signal } from "@preact/signals";
import { STAMP_BOX, stampImage } from "../slip.ts";
import styles from "./delivery.module.css";

/** How the slip comes in beside the delivered piece (ms). */
export const SLIP_IN = { delay: 200, duration: 700 };

/** What the delivery screen shows: the game writes it, the screen follows. */
export const delivery = {
  open: signal(false),
  progress: signal(""),
  /** The slip, unstamped, as an object URL. */
  slip: signal(""),
  /** The shop's stamp has come down on the slip. */
  stamped: signal(false),
  canShare: signal(false),
  /** How keeping the piece on the shelf is going. */
  kept: signal<"not yet" | "keeping" | "kept" | "failed">("not yet"),
};

/** What the delivery screen asks of the game. */
export interface DeliveryActions {
  share(): void;
  /** The commemorative photo, saved as a file. */
  save(): void;
  keep(): void;
  toTitle(): void;
  next(): void;
}

const KEEP_LABELS = {
  "not yet": "棚に飾る",
  keeping: "棚に飾る",
  kept: "飾りました",
  failed: "飾れませんでした",
};

/** The delivery slip, stamped by the shop, with what to do with the piece. */
export function mountDelivery(root: HTMLElement, actions: DeliveryActions): void {
  render(<DeliveryScreen actions={actions} />, root);
}

function DeliveryScreen({ actions }: { actions: DeliveryActions }) {
  const panel = useRef<HTMLElement>(null);
  const open = delivery.open.value;
  useEffect(() => {
    if (!open) return;
    panel.current?.animate(
      [{ opacity: 0, translate: "40px 0" }, { opacity: 1, translate: "0 0" }],
      { ...SLIP_IN, easing: "cubic-bezier(0.3, 0.7, 0.4, 1)", fill: "backwards" },
    );
  }, [open]);
  if (!open) return null;
  const kept = delivery.kept.value;
  return (
    <section class={styles.delivery} ref={panel}>
      <div class={styles.slip}>
        <img class={styles.slipImage} src={delivery.slip} alt="ねこ商会の納品票" />
        {delivery.stamped.value && <Stamp />}
      </div>
      {delivery.progress.value && <p class={styles.progress} role="status">{delivery.progress}</p>}
      <div class={`row ${styles.actions}`}>
        {delivery.canShare.value && (
          <button
            type="button"
            class="reset"
            onClick={() => actions.share()}
          >
            共有
          </button>
        )}
        <button type="button" class="reset" onClick={() => actions.save()}>記念撮影</button>
        <button
          type="button"
          class="reset"
          disabled={kept !== "not yet"}
          onClick={() => actions.keep()}
        >
          {KEEP_LABELS[kept]}
        </button>
      </div>
      <div class={`row ${styles.actions}`}>
        <button type="button" class="reset" onClick={() => actions.toTitle()}>
          タイトルに戻る
        </button>
        <button type="button" class="submit" onClick={() => actions.next()}>次をこねる</button>
      </div>
    </section>
  );
}

/** Pon: the stamp comes down onto the slip. */
function Stamp() {
  const stamp = useRef<HTMLImageElement>(null);
  useEffect(() => {
    stamp.current?.animate(
      [
        { opacity: 0, transform: "scale(2.2) rotate(-18deg)" },
        { opacity: 1, transform: "scale(0.92) rotate(0deg)", offset: 0.7 },
        { opacity: 1, transform: "scale(1) rotate(0deg)" },
      ],
      { duration: 320, easing: "ease-out" },
    );
  }, []);
  return (
    <img
      class={styles.stamp}
      ref={stamp}
      src={stampImage(256).toDataURL()}
      alt=""
      style={{
        left: `${STAMP_BOX.left * 100}%`,
        top: `${STAMP_BOX.top * 100}%`,
        width: `${STAMP_BOX.width * 100}%`,
      }}
    />
  );
}
